import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { AllowanceClient } from '@frontend/budget/api';
import { BudgetAllowance } from '@frontend/budget/types';
import { CertificationClient } from '@frontend/certificate/api';
import { Certification, UserCertification } from '@frontend/certificate/types';
import { Feature } from '@frontend/functionality/types';
import { IGroup } from '@frontend/group/types';
import { IProduct, ProductType } from '@frontend/product/types';
import { StockAvailabilityClient } from '@frontend/stock/api';
import { StockAvailability } from '@frontend/stock/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useMemo, useState } from 'react';

import { canOrderProduct } from '../../utils/product-order-permission';
import { useScreen } from '../useScreen';

export const useSelectQuantity = () => {
    const { updateWizardData, store, onSubmit, onBack, navigationState, wizardData } = useScreen();
    const { userOverwrite } = wizardData;
    const isRecycleMode = wizardData.browseProductsContext?.feature === Feature.RECYCLE_TRANSACTION;

    // Product from wizard store (set by browse-products-by-categories)
    const product: IProduct | null = store?.data?.selectedProduct ?? null;
    const categoryStack: IGroup[] = store?.data?.browseProductsNavigation?.categoryStack ?? [];

    const [allowance, setAllowance] = useState<BudgetAllowance | null>(null);
    const [certification, setCertification] = useState<Certification | null>(null);
    const [stockAvailability, setStockAvailability] = useState<StockAvailability | null>(null);
    const [amount, setAmount] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isCheckingOrderPermission, setIsCheckingOrderPermission] = useState(false);
    const canOrder = store?.data?.selectedProductCanBeOrdered === true;
    const orderFallbackStep = store?.steps?.find((step) => step.screen === ScreenType.SELECT_QUANTITY && step.trigger === ScreenTrigger.SUBMIT_FALSE);

    const currentSpotId = navigationState.spot?.id;

    const userId = navigationState.user?.id;
    const accountId = navigationState.user?.accountId;
    const userAuth = navigationState.user?.auth;
    const spotId = navigationState.spot?.id;
    const selectedCertification = store?.data?.selectedCertification as Certification | null | undefined;

    // Fetch allowance and stock data for the selected product
    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            setCertification(null);
            if (!product || !userId || !accountId || isRecycleMode) {
                setAllowance(null);
                setStockAvailability(null);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            const certificationUserId = userOverwrite?.id ?? userId;
            const certificationRequest =
                product.type === ProductType.EQUIPMENT
                    ? selectedCertification
                        ? Promise.resolve({ [product.id]: selectedCertification } as UserCertification)
                        : DriverAuthenticationManager.getInstance()
                              .waitForToken()
                              .then((iotToken) => CertificationClient.postGetProductCertification(accountId, certificationUserId, [product.id], iotToken))
                              .catch((): UserCertification => ({}))
                    : Promise.resolve(null);

            try {
                const [stockResponse, allowanceResponse, certificationResponse] = await Promise.all([
                    StockAvailabilityClient.postGetProductAvailability(accountId, [product.id]),
                    spotId && userAuth
                        ? AllowanceClient.postGetUserAllowancesV2([product.id], { spot_id: spotId, user_id: userOverwrite?.id ?? undefined }, userAuth)
                        : Promise.resolve(null),
                    certificationRequest
                ]);

                if (cancelled) return;

                setStockAvailability(stockResponse[product.id] ?? null);
                setAllowance(allowanceResponse?.[product.id] ?? null);
                setCertification(certificationResponse?.[product.id] ?? null);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void fetchData();
        return () => {
            cancelled = true;
        };
    }, [product, userId, accountId, userAuth, spotId, isRecycleMode, userOverwrite?.id, selectedCertification]);

    // Calculate stock info for current spot
    const stockInfo = useMemo(() => {
        if (!stockAvailability || !stockAvailability.stocks) {
            return { hasStockInCurrentSpot: false, hasStockInOtherSpots: false, currentSpotStocks: [], otherSpotIds: [] };
        }

        const currentSpotStocks = stockAvailability.stocks.filter((s) => s.spot_id === currentSpotId);
        const otherSpotStocks = stockAvailability.stocks.filter((s) => s.spot_id !== currentSpotId);

        const currentSpotQuantity = currentSpotStocks.reduce((sum, s) => sum + s.quantity, 0);
        const otherSpotQuantity = otherSpotStocks.reduce((sum, s) => sum + s.quantity, 0);

        const otherSpotIds = [...new Set(otherSpotStocks.filter((s) => s.quantity > 0).map((s) => s.spot_id))];

        return {
            hasStockInCurrentSpot: currentSpotQuantity > 0,
            hasStockInOtherSpots: otherSpotQuantity > 0,
            currentSpotStocks,
            currentSpotQuantity,
            otherSpotIds
        };
    }, [stockAvailability, currentSpotId]);

    // Max stock is based on current spot only
    const maxStock = useMemo<number>(() => {
        return stockInfo.currentSpotQuantity ?? 0;
    }, [stockInfo.currentSpotQuantity]);

    // Max budget allowance
    const maxBudget = useMemo<number>(() => {
        if (!allowance) return Infinity;
        return allowance.quantity;
    }, [allowance]);

    // Total max (minimum of budget and stock)
    const totalMax = useMemo<number>(() => {
        if (product?.type === ProductType.CONSUMABLE && canOrder && !stockInfo.hasStockInCurrentSpot) return maxBudget;
        return Math.min(maxStock, maxBudget);
    }, [maxStock, maxBudget, canOrder, product?.type, stockInfo.hasStockInCurrentSpot]);

    // Reset amount when totalMax changes
    useEffect(() => {
        if (totalMax >= 1 && amount === 0) setAmount(1);
        if (totalMax < amount) setAmount(Math.max(1, totalMax));
    }, [totalMax, amount]);

    // Check if amount change is disabled
    const isAmountDisabled = (addition: number): boolean => {
        const newAmount = amount + addition;
        return newAmount < 1 || newAmount > totalMax;
    };

    // Change amount
    const changeAmount = (newAmount: number) => {
        if (newAmount >= 1 && newAmount <= totalMax) {
            setAmount(newAmount);
        }
    };

    const navigateBack = () => {
        // Clear selected product and go back
        updateWizardData({
            selectedProduct: null,
            selectedProductStocks: [],
            selectedAmount: 1,
            selectedAllowance: null,
            selectedCertification: null,
            selectedProductCanBeOrdered: false
        });
        onBack();
    };

    const confirmQuantitySelection = async () => {
        const shouldOrder = product?.type === ProductType.CONSUMABLE && canOrder && !stockInfo.hasStockInCurrentSpot;
        if (shouldOrder) {
            if (!navigationState.user) return;
            if (!orderFallbackStep) return;
            setIsCheckingOrderPermission(true);
            const canOrderProductResult = await canOrderProduct(navigationState.user.accountId, product.id, navigationState.user.auth);
            setIsCheckingOrderPermission(false);
            if (!canOrderProductResult) return;
            updateWizardData({
                browseProductsNavigation: {
                    categoryStack,
                    leafSelectedProduct: null,
                    selectedProductStocks: []
                },
                selectedProduct: product,
                selectedProductStocks: [],
                selectedAmount: amount,
                selectedAllowance: allowance,
                selectedCertification: certification,
                selectedProductCanBeOrdered: false
            });
            onSubmit(orderFallbackStep);
            return;
        }
        if (product && (isRecycleMode || (stockInfo.hasStockInCurrentSpot && hasCertification))) {
            updateWizardData({
                browseProductsNavigation: {
                    categoryStack,
                    leafSelectedProduct: null,
                    selectedProductStocks: []
                },
                selectedProduct: product,
                selectedProductStocks: isRecycleMode ? [] : stockInfo.currentSpotStocks,
                selectedAmount: isRecycleMode ? 1 : amount,
                selectedAllowance: isRecycleMode ? null : allowance,
                selectedCertification: isRecycleMode ? null : certification
            });
            const continueScreen = store?.steps?.find((step) => step.screen === ScreenType.SELECT_QUANTITY && step.trigger === ScreenTrigger.SUBMIT);
            onSubmit(continueScreen);
        }
    };

    const hasCertification = isRecycleMode || product?.type !== ProductType.EQUIPMENT || certification?.certified === true;
    const isOrderFallback = product?.type === ProductType.CONSUMABLE && canOrder && !stockInfo.hasStockInCurrentSpot;
    const canConfirm = isOrderFallback
        ? amount >= 1 && amount <= maxBudget
        : isRecycleMode
          ? product !== null
          : product !== null && stockInfo.hasStockInCurrentSpot && hasCertification && amount >= 1 && amount <= totalMax;

    return {
        product,
        isLoading,
        amount,
        changeAmount,
        isAmountDisabled,
        maxStock,
        maxBudget: allowance,
        certification,
        hasCertification,
        totalMax,
        stockInfo,
        navigateBack,
        confirmQuantitySelection,
        canConfirm,
        isCheckingOrderPermission,
        isRecycleMode,
        isOrderFallback,
        hasOrderFallbackStep: !!orderFallbackStep
    };
};
